// src/sim/tick.ts — pure simulation step (FOUND-02, FOUND-07).
//
// P3 stage order (locked — downstream subscribers depend on this):
//   0. team-switch transition advance (flipRolesAndReset at window end, constant-step lerp otherwise)
//   1. round FSM advance (round-ended + team-switched emissions, bindTeamSwitchTargets on A→E)
//   2. motion-lights advance (P2 unchanged; emits motion-light-on/off)
//   3. headstart movement gate (drops hunter intent.move during phase='headstart')
//   4. per-entity move + facing + torch + entity-moved (P2 unchanged; skipped when inTeamswitchTransition)
//   5. stamina drain/regen (emits stamina-depleted on 1→0)
//   6. catch detection (phase='active' only; emits catch)
//   7. escort leash motion (no entity-moved event — WARNING-11)
//   8. drop-off (emits hider-parked)
//   9. free-all (emits hider-released)
//   10. foxguard 4-condition + streak + shout (emits foxguard-called)
//   11. hide-candidate DERIVED-FIELD write (reuses the e0 candidate computed at stage 13a
//        when the e0 intent was processed; falls back to a fresh compute only when no e0
//        intent existed this tick — WARNING-7 single-compute-per-tick guarantee)
//   12. peek auto-release check (emits peek-ended; sets peekSuppressedUntilRelease=true
//        so stage 13b cannot re-engage peek on the next tick — BLOCKER-3 finger-stuck guard)
//   13. hide intent consumption (toggleHide → hiding-entered/exited; dropToFloor → on-floor spot;
//        peekHeld → peek-started/ended edges via applyPeekIntent which itself honours
//        peekSuppressedUntilRelease — BLOCKER-3). Note: stages 13a + 13b run INSIDE the
//        per-entity loop alongside stages 3–5 so we have the final status for peek.
//   14. torch FOV recompute (P2 unchanged)
//
// P4 stage order (appended after stage 14, in documented order):
//   15. (merged into 22) — fear triggers read from mapData.fearTriggers; no separate stage.
//   16. Per-AI-entity perception build + BT decide (cadence-gated per D-05).
//       _recentSoundEventsThisTick cleared at start; populated by stages 17/21.
//   17. Awareness step — advanceAwareness per (hunter, visible-hider) pair;
//       stepStabilityCheck; emits awareness-changed; writes team blackboard at Alerted/Spotted;
//       emits shout at Spotted-stable; populates _hunterLastSightingTick (BLOCKER-4).
//   18. Blackboard decay — pruneBlackboard for both teams.
//   19. (merged into 16) — utility scoring runs inside AIController.decide.
//   20. (merged into 16) — BT step runs inside AIController.decide.
//   21. Shout propagation + dog-barked event translation (INFO-11).
//       Dog shout→dog-barked; human shout→shout; both pushed to _recentSoundEventsThisTick
//       for next-tick perception.
//   22. Fear accumulators — advanceFear per non-NPC entity; applies entity.fear delta;
//       appends boundary events. lastSightingTick from _hunterLastSightingTick (BLOCKER-4).
//   23. Adult commentary rate-limit — adultComment intent → adult-commented event if
//       global cooldown elapsed; updates world.lastAdultCommentaryTick.
//
// WARNING-7 fix (single compute per tick for hide-candidate):
//   Stage 13a hoists `occupied: Set<string>` once per tick (O(n) over entities).
//   For the player entity 'e0' when role=hider + status=free, findActiveHidingSpot is
//   called ONCE — the result drives both the intent consumption AND the derived-field
//   write at stage 11. Stage 11 is a pure write path for e0; it only recomputes when
//   no e0 intent was present (headless tests, stand-still ticks).
//
// BLOCKER-3 fix (peek auto-release oscillation):
//   Stage 12 checkPeekAutoRelease sets Entity.peekSuppressedUntilRelease=true when
//   auto-releasing. Stage 13b applyPeekIntent (Plan 05) ignores the peek intent while
//   that flag is true; it clears the flag when peekHeld=false. This breaks the loop
//   where a still-held finger would otherwise re-engage peek one tick later.
//
// BLOCKER-4 fix (_hunterLastSightingTick):
//   Module-level Map populated in Stage 17 (awareness write) whenever a hunter reaches
//   Alerted or Spotted. Passed into advanceFear in Stage 22 as lastSightingTick.
//   Cleared on round-started via resetHunterLastSightingTickForRound().
//
// Event ordering contract (consumers MUST assume this order):
//   1. motion-light-on/off
//   2. round-ended + team-switched (at active→ended)
//   3. round-started (at ended→headstart-new-round)
//   4. entity-moved
//   5. torch-flicked
//   6. stamina-depleted
//   7. catch
//   8. hider-parked
//   9. hider-released
//   10. foxguard-called
//   11. peek-ended (auto-release)
//   12. hiding-entered / hiding-exited (intent-driven)
//   13. peek-started / peek-ended (intent-driven)
//   [P4 additions — after existing 13]
//   14. awareness-changed (Stage 17)
//   15. shout / dog-barked (Stage 21)
//   16. fear-threshold-crossed (Stage 22)
//   17. adult-commented (Stage 23)
//
// Invariants:
//   - No side effects: returns a new WorldState; does not mutate `state`.
//   - No DOM, no Phaser, no timers (ESLint Plan 02 enforces).
//   - Deterministic given the same inputs: (state, intents, dt) -> (next, events).
//   - `dt` is provided by the render-layer accumulator (TICK_MS/1000 = 1/30).
//
// Source: Phase 0 RESEARCH.md §Pattern 1 (Fixed-Timestep Accumulator);
//         .planning/phases/03-core-sim-primitives/03-RESEARCH.md §System Architecture Diagram;
//         .planning/phases/04-ai/04-10-PLAN.md Task 1.

import type { WorldState, Entity, FovSnapshot, LightsState, DerivedState } from '../shared/types/world';
import type { Intent } from '../shared/types/intent';
import type { GameEvent, EventTag } from '../shared/types/events';
import type { MapData, HidingSpotAuth } from '../shared/types/map';
import type { Vec2 } from '../shared/types/intent';
import { buildPerception } from './ai/perception';
import {
  advanceAwareness,
  decayAwareness,
  classifyLevel,
  emitBoundaryEvent,
  stepStabilityCheck,
} from './ai/awareness/accumulator';
import { pruneBlackboard, upgradeOrWriteBlackboardEntry } from './ai/blackboard';
import { advanceFear } from './ai/fear/accumulators';
import { isShoutAudible } from './ai/coordination/shout';
import { getAiController, initAiController, resetAiControllers, clearAiControllers } from './controllers/registry';
import { resetPathingForTick, resetPathingForRound } from './ai/pathing';
import { registerP4ResetCallback } from './ai/p4-caches';
import { getProfile, hasProfile, DEFAULT_CHARACTER_ID } from './characters/profiles';
import {
  hasContentBundle,
  getContentBundle,
  pickLineIndex,
  getTuning,
} from './content/cache';
import {
  P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS,
  P4_HUNTER_DECIDE_PERIOD_TICKS,
  P4_AI_AMBIENT_LIGHT_FLOOR,
} from './ai/constants';
import {
  P1_DEFAULT_BASE_SPEED,
  P1_SPRINT_MULTIPLIER,
  P1_DEFAULT_TURN_RATE,
  P1_CHARACTER_RADIUS,
  clampMotionAgainstWalls,
  lerpFacing,
  lightSpeedRamp,
} from './movement';
import {
  advanceMotionLights,
  computeTorchFov,
  lightLevelAt,
  allocBitset,
  type TorchLightSource,
} from './lighting';
import { sightabilityOf } from './perception/sightability';
import { createRng, type SimRng } from './rng';
import {
  advanceRoundFsm,
  flipRolesAndReset,
  bindTeamSwitchTargets,
  applyTeamSwitchLerp,
} from './round';
import {
  detectCatches,
  applyEscortLeash,
  applyDropOff,
  applyFreeAll,
} from './catch';
import {
  evaluateFoxguardConditions,
  updateFoxguardStreak,
  pickShouter,
} from './foxguard';
import {
  findActiveHidingSpot,
  applyHideToggle,
  applyPeekIntent,
  checkPeekAutoRelease,
  applyDropToFloorIntent,
} from './hiding';
import { applyStamina } from './stamina';

// Module-level preallocated bitset — one allocation per process lifetime per map shape.
// Pitfall 11: per-tick `new Uint32Array()` allocates ~470 ints × 30 Hz = 14k ints/s of GC
// pressure on mid-range Android. Reuse-in-place is mandatory.
let _torchBitset: Uint32Array | null = null;
function getTorchBitset(widthInTiles: number, heightInTiles: number): Uint32Array {
  const needed = Math.ceil((widthInTiles * heightInTiles) / 32);
  if (!_torchBitset || _torchBitset.length !== needed) {
    _torchBitset = allocBitset(widthInTiles, heightInTiles);
  }
  return _torchBitset;
}

// Module-level SimRng tree cached by rngSeed for foxguard shouter picks.
// One PRNG tree per process; rebuilt if the seed changes (e.g. between tests).
let _rngCache: { seed: number; rng: SimRng } | null = null;
function getRng(seed: number): SimRng {
  if (!_rngCache || _rngCache.seed !== seed) {
    _rngCache = { seed, rng: createRng(seed) };
  }
  return _rngCache.rng;
}

/**
 * WR-06 fix: replay-equality determinism. _rngCache is module-scoped and the
 * cached SimRng tree is MUTABLE — its internal streams advance as draws are
 * made (e.g. rng.foxguard). A second simulation run with the same rootSeed
 * would therefore resume drawing from the mid-stream position left by run 1,
 * not from a fresh seed.
 *
 * Registered as a p4 reset callback so createInitialWorldState
 * (→ resetP4CachesForNewSim) forces a rebuild of the tree on the next
 * getRng() call. Verified by sim.phase4-integration "S8: replay equality".
 */
function resetRngCacheForNewSim(): void {
  _rngCache = null;
}

// ---------------------------------------------------------------------------
// P6 D-04 — sim-tier 'character-spoke' emit helper (Task 4 / BLOCKER-1).
// ---------------------------------------------------------------------------
//
// Called by the trigger-event sites below (catch / free-all / hider-released
// / foxguard-called / round-started / round-ended) AFTER the existing event
// has already been pushed into events[]. The plan's mapping table:
//
//   catch              → speaker = ev.hunterId,   tag = 'catch'
//   free-all           → speaker = ev.liberatorId, tag = 'free-all'
//   hider-released     → speaker = ev.liberatorId, tag = 'free-all'
//                        (CONTEXT mapping — quotes are authored only for
//                        'free-all', not 'hider-released'; reuse the bucket)
//   foxguard-called    → speaker = ev.shouterId,  tag = 'foxguard-called'
//   round-started      → speaker = 'e0',          tag = 'round-start'
//   round-ended        → speaker = 'e0',          tag = 'round-end'
//
// Emission is golden-hash-stable: events[] is excluded from canonical
// WorldState hashing (see sim.golden-hash.test.ts which hashes
// JSON.stringify(state) only). lineIndex is drawn from rng.waffle (P0
// sub-seed) for replay determinism. Pre-hydration safety: hasContentBundle()
// guard short-circuits before any cache read, so a tick called against an
// unhydrated cache neither emits nor throws (subject to the broader
// constraint that other tick stages already require hydrated profiles).
function maybeEmitCharacterSpoke(
  entityId: string,
  eventTag: EventTag,
  tickNumber: number,
  entitiesById: Record<string, Entity>,
  rng: SimRng,
  events: GameEvent[],
): void {
  if (!hasContentBundle()) return;
  const entity = entitiesById[entityId];
  if (!entity) return;
  // P6 D-11 parity (Plan 06-12, UAT Gap 4): hidden hiders are silent.
  // Mirrors src/render/audio/footsteps.ts:150 for the speech path. By audit
  // (.planning/debug/hidden-hiders-emit-bubbles.md) no current call site
  // emits character-spoke for a hidden speaker, so this is a defensive
  // short-circuit — golden-hash unchanged. Pinned by the unit test
  // src/sim/__tests__/tick.maybeEmitCharacterSpoke.hidden-guard.test.ts.
  if (entity.status === 'hiding') return;
  const characterId = entity.characterId;
  if (!characterId) return;
  const bundle = getContentBundle();
  const quote = bundle.quotes.find(
    (q) => q.characterId === characterId && q.eventTag === eventTag,
  );
  if (!quote || quote.lines.length === 0) return;
  const lineIndex = pickLineIndex(characterId, eventTag, rng.waffle, quote.lines.length);
  if (lineIndex < 0) return;
  events.push({
    type: 'character-spoke',
    entityId,
    characterId,
    eventTag,
    lineIndex,
    tick: tickNumber,
  });
}

// ---------------------------------------------------------------------------
// P4 module-level caches (04-10-PLAN.md Task 1 Step 2)
// ---------------------------------------------------------------------------

/**
 * P4 awareness cache entry — per (observer, target) pair.
 * Lives in a module-level Map keyed by rngSeed (one cache per round/scenario).
 * Pattern 04-PATTERNS.md L596-604: NO per-tick Map allocation (Pitfall 11).
 */
interface AwarenessCacheEntry {
  awareness: number;
  stableTicks: number;
  lastSeenTick: number;
}

// Outer key: observerId; inner key: targetId.
let _awarenessCache: {
  seed: number;
  data: Map<string, Map<string, AwarenessCacheEntry>>;
} | null = null;

function getAwarenessCacheData(seed: number): Map<string, Map<string, AwarenessCacheEntry>> {
  if (!_awarenessCache || _awarenessCache.seed !== seed) {
    _awarenessCache = { seed, data: new Map() };
  }
  return _awarenessCache.data;
}

function resetAwarenessCacheForRound(): void {
  // Null out the cache entirely so getAwarenessCacheData always returns a fresh Map
  // for the next round — even if the rngSeed is the same (e.g. replay-equality tests).
  _awarenessCache = null;
}

/**
 * Sound events accumulated THIS tick by stages 17/21.
 * Cleared at the start of each tick; surfaced via buildPerception as heard[].
 * Design: heard lags 1 sim tick (33 ms — imperceptible).
 */
let _recentSoundEventsThisTick: Array<{
  kind: 'shout' | 'dog-bark';
  sourceId: string;
  position: Vec2;
  tick: number;
}> = [];

/**
 * WR-01 / WR-05 fix: reset sound events on round-start alongside the other P4
 * per-round caches. Without this, a shout emitted just before flipRolesAndReset
 * or at the very end of a round leaks into the new round's first perception
 * tick. See registerP4ResetCallback wiring below.
 */
function resetRecentSoundEventsForRound(): void {
  _recentSoundEventsThisTick = [];
}

/**
 * BLOCKER-4: tracks the tick at which each hunter last observed ANY hider at awareness
 * level Alerted or higher (i.e. had a genuine sighting event). Used by advanceFear as
 * the `lastSightingTick` arg for the isolation-fear accumulator (D-20, FEAR-02).
 * Mirrors the _awarenessCache lifecycle: cleared on round-started.
 */
const _hunterLastSightingTick = new Map<string, number>();
function resetHunterLastSightingTickForRound(): void {
  _hunterLastSightingTick.clear();
}

/**
 * BLOCKER-1 (D-16): tracks which blackboard entry id each hunter is currently targeting.
 * Populated after Stage 16 (BT decide) by reading agent.targetBlackboardEntryId.
 * Used to count duplicates for computeInvestigateUtility.
 */
const _hunterTargetBlackboardEntryId = new Map<string, string | null>();
function resetHunterTargetBlackboardEntryIdForRound(): void {
  _hunterTargetBlackboardEntryId.clear();
}

/**
 * CR-01 fix: tracks AI entities that shouted this tick. Replaces the previous
 * `_aiShoutThisTick` flag mutated onto Entity records (which violated the
 * "no side effects on input state" invariant in the header comment). Populated
 * in Stage 16 and drained in Stage 21; cleared at the top of every tick.
 */
const _aiShoutsThisTick = new Set<string>();

/**
 * WR-01 fix: also reset the AI shouts set on round-start so a lingering shout
 * intent cannot re-fire into the freshly-flipped round via Stage 21.
 */
function resetAiShoutsForRound(): void {
  _aiShoutsThisTick.clear();
}

/** Returns count of hunters (other than selfId) already heading to a given blackboard entry. */
function huntersAlreadyHeadingToEntry(entryId: string, selfId: string): number {
  let count = 0;
  for (const [hunterId, targetId] of _hunterTargetBlackboardEntryId) {
    if (hunterId === selfId) continue;
    if (targetId === entryId) count++;
  }
  return count;
}

// Register all P4 per-round cache resets with the shared p4-caches hub.
// world.ts calls resetP4CachesForNewSim() which invokes these callbacks,
// ensuring determinism across replay-equality tests (same seed, fresh cache).
registerP4ResetCallback(resetAwarenessCacheForRound);
registerP4ResetCallback(resetHunterLastSightingTickForRound);
registerP4ResetCallback(resetHunterTargetBlackboardEntryIdForRound);
registerP4ResetCallback(resetPathingForRound);
// WR-01 / WR-05: sound events and shout set must reset on round-start so a
// shout from the previous round does not leak into Stage 16 perception for
// the first tick of the next round.
registerP4ResetCallback(resetRecentSoundEventsForRound);
registerP4ResetCallback(resetAiShoutsForRound);
// WR-06: rebuild _rngCache on new sim so replay-equality tests (same seed,
// fresh call to createInitialWorldState) don't resume a mid-stream SimRng.
registerP4ResetCallback(resetRngCacheForNewSim);

// Export reset utilities so tests and GameScene can trigger round resets
export {
  resetAwarenessCacheForRound,
  resetHunterLastSightingTickForRound,
  resetHunterTargetBlackboardEntryIdForRound,
  clearAiControllers,
  resetAiControllers,
  initAiController,
};

/**
 * Test-only export of the module-private maybeEmitCharacterSpoke helper.
 *
 * Plan 06-12 (UAT Gap 4): the D-11 parity defensive guard ("hidden hiders are
 * silent — speech path mirror of footsteps.ts:150") is verified by a unit test
 * that drives this helper directly against a hand-constructed entity record.
 * Underscore prefix + ForTest suffix follows the conventional "module-private,
 * test-public" pattern used elsewhere in src/sim/**.
 */
export const _maybeEmitCharacterSpokeForTest = maybeEmitCharacterSpoke;

export interface IntentBundle {
  entityId: string;
  intent: Intent;
}

export interface TickResult {
  state: WorldState;
  events: GameEvent[];
}

/**
 * Pure tick function — P3 14-stage orchestrator. See the header comment for the
 * full stage order and event ordering contract.
 *
 * Optional `mapData` enables sim-side axis-separated wall clamping + lighting
 * + catch/drop-off/free-all/foxguard/hiding stages. When omitted (default),
 * motion is unclamped and those stages are no-ops — this preserves the existing
 * golden-hash determinism fixture which runs without a map bound.
 */
export function tick(
  state: WorldState,
  intents: IntentBundle[],
  dt: number,
  mapData?: MapData | null,
): TickResult {
  const events: GameEvent[] = [];
  const nextTick = state.tick + 1;
  let nextEntities: Record<string, Entity> = { ...state.entities };
  let nextRound = { ...state.round };
  // Track blackboards in a mutable variable so Stage 0 flip propagates to Stage 17/18 (D-33).
  // Without this, flipRolesAndReset resets the boards but line 662 re-reads state.teamBlackboards.
  let nextFlippedBlackboards: WorldState['teamBlackboards'] | null = null;

  // P6 D-04 BLOCKER-1: hoist the rng tree so stages 0/1/6/9/10 can all share
  // it for the 'character-spoke' line picker. getRng is module-cached per
  // seed, so this is idempotent — calling it earlier does not affect the
  // foxguard rng draws that happen later in stage 10.
  const rngForTick = getRng(state.rngSeed);

  // P4 Stage 16 pre-step: clear _recentSoundEventsThisTick at start of each tick.
  // Stage 17/21 will repopulate it; perception.heard[] for the NEXT tick reads from it.
  _recentSoundEventsThisTick = [];

  // CR-01: clear the per-tick shout set. Stage 16 populates, Stage 21 reads.
  _aiShoutsThisTick.clear();

  // P4: reset per-tick pathing budget counter (Threat T-04-08).
  resetPathingForTick(nextTick);

  // P4: If this is the very first tick of a new simulation run (state.tick === 0),
  // reset all per-round caches to avoid stale state from a previous run with the same seed.
  // This matters for replay-equality tests that call createInitialWorldState + tick twice.
  if (state.tick === 0) {
    resetAwarenessCacheForRound();
    resetHunterLastSightingTickForRound();
    resetHunterTargetBlackboardEntryIdForRound();
    resetPathingForRound();
    // WR-01 / WR-05: reset sound-event carryover when replay re-starts at tick=0.
    resetRecentSoundEventsForRound();
    resetAiShoutsForRound();
  }

  // STAGE 0 — team-switch transition advance.
  //
  //   - If phase='ended' AND currentTick >= endTick + P3_TEAMSWITCH_DURATION_TICKS:
  //     flipRolesAndReset + emit round-started (new round headstart).
  //   - Else if phase='ended': constant-step lerp each entity toward teamSwitchTarget.
  if (state.round.phase === 'ended' &&
      nextTick >= state.round.endTick + getTuning().teamswitchDurationTicks) {
    const flipped = flipRolesAndReset({ ...state, entities: nextEntities, round: nextRound }, nextTick);
    nextEntities = flipped.state.entities;
    nextRound = flipped.state.round;
    // D-33: capture the cleared blackboards so Stage 17 initialisation below uses them,
    // not the stale state.teamBlackboards (which still contains the pre-flip entries).
    nextFlippedBlackboards = flipped.state.teamBlackboards;
    for (const ev of flipped.events) {
      events.push(ev);
      // P6 D-04 — round-started: speaker = 'e0' (local player), tag = 'round-start'.
      // Plan 06-10 (B2 audio-leak fix): at flipRolesAndReset time the new
      // round's phase='headstart', so this emission would leak audio waffle
      // into the silent countdown. Gate to phase==='active' — UAT Gap 1.
      // (The 'round-started' event itself remains in events[] for the FSM
      // contract; only the speech emission is suppressed.)
      if (ev.type === 'round-started' && nextRound.phase === 'active') {
        maybeEmitCharacterSpoke('e0', 'round-start', nextTick, nextEntities, rngForTick, events);
      }
    }
    // P4: reset per-round AI caches on round-started (D-33 within-round-only adaptation).
    resetAwarenessCacheForRound();
    resetHunterLastSightingTickForRound();
    resetHunterTargetBlackboardEntryIdForRound();
    resetPathingForRound();
    // WR-01 / WR-05: sound-event + shout carryover must NOT leak into the
    // freshly-flipped round's first perception tick.
    resetRecentSoundEventsForRound();
    resetAiShoutsForRound();
    resetAiControllers();
  } else if (state.round.phase === 'ended') {
    const startTick = state.round.endTick;
    const finishTick = startTick + getTuning().teamswitchDurationTicks;
    nextEntities = applyTeamSwitchLerp(nextEntities, nextTick, startTick, finishTick);
  }

  // STAGE 1 — round FSM advance (only after potential flip so phase state is fresh).
  {
    const caughtCount = Object.values(nextEntities)
      .filter((e) => e.role === 'hider' && e.status === 'caught').length;
    const fsm = advanceRoundFsm(nextRound, caughtCount, nextTick);
    const wasActive = nextRound.phase === 'active';
    const nowEnded = fsm.next.phase === 'ended';
    nextRound = fsm.next;
    for (const ev of fsm.events) {
      events.push(ev);
      // P6 D-04 — round-ended: speaker = 'e0' (local player), tag = 'round-end'.
      // (round-started is emitted only by flipRolesAndReset in stage 0; the
      // FSM advance here only ever emits round-ended + team-switched.)
      //
      // Plan 06-10 inspection result: at this emission point nextRound has
      // ALREADY been assigned fsm.next (line 493) which carries phase='ended'
      // for the active→ended transition tick (advanceRoundFsm/fsm.ts L51-52,
      // L66-67 set next.phase='ended' BEFORE pushing the round-ended event).
      // So nextRound.phase === 'ended' at this emission — needs the gate.
      // Per CONTEXT D-15 the team-switch ('ended') window is also a
      // diegetic-silent transition; suppressing the round-end speech here
      // is consistent. UAT Gap 1 (M10 inclusion).
      if (ev.type === 'round-ended' && nextRound.phase === 'active') {
        maybeEmitCharacterSpoke('e0', 'round-end', nextTick, nextEntities, rngForTick, events);
      }
    }
    if (wasActive && nowEnded && mapData) {
      // WR-03: salt with the UPCOMING round number (roundNumber + 1) to match
      // flipRolesAndReset which sets nextRound.roundNumber = roundNumber + 1,
      // and to match round 1's boot salt (GameScene.buildRosterState, salt=1).
      // Passing the ending-round number (off-by-one) made rounds 1 and 2 share
      // the same zone shuffle, breaking D-02 per-round variance for the first
      // team-switch transition. Regression-locked by tick-p3-integration.test.ts Test 16.
      nextEntities = bindTeamSwitchTargets(nextEntities, mapData, state.rngSeed, state.round.roundNumber + 1);
    }
  }

  // STAGE 2 — motion lights.
  // P2 D-16 event-ordering contract: motion-light-on/off fires BEFORE entity-moved
  // + torch-flicked (entity loop below), all sharing `tick = state.tick + 1`.
  const lightsForTick = mapData?.lights ?? [];
  const entityList = Object.values(nextEntities);
  const motionRes = advanceMotionLights(
    state.lightsState ?? {},
    lightsForTick,
    entityList,
    nextTick,
    mapData?.tileWidth ?? 0,
  );
  const nextLightsState: LightsState = motionRes.next;
  for (const ev of motionRes.events) events.push(ev);

  // === WARNING-7 hoisted hide-candidate infrastructure ===
  // Compute the occupied-spot set ONCE for the whole tick. Reused by stage 13a
  // (intent consumption for every hider with an intent) and stage 11 (derived-
  // field write for e0). This avoids the O(n) re-scan the previous plan performed
  // twice.
  const occupied: Set<string> = (() => {
    const s = new Set<string>();
    for (const e of Object.values(nextEntities)) {
      if (e.hidingSpotId !== null) s.add(e.hidingSpotId);
    }
    return s;
  })();

  // Track the e0 hide-candidate computed during stage 13a for stage 11 reuse.
  // undefined = stage 13a did not run for e0 (no intent); null = ran, no spot;
  // HidingSpotAuth = ran, found a spot. Stage 11 reuses only when !== undefined.
  let e0CandidateComputed: HidingSpotAuth | null | undefined = undefined;

  // STAGES 3 + 4 + 5 + 13a + 13b — per-entity move + facing + torch + stamina
  // + hide/peek intent consumption (headstart-gated).
  for (const { entityId, intent } of intents) {
    const current = nextEntities[entityId];
    if (!current) continue;
    // Sim owns motion during team-switch; the controller's intent is ignored.
    if (current.inTeamswitchTransition) continue;

    // STAGE 3 — movement gates:
    //   (a) headstart gate: hunters cannot move during the headstart window.
    //   (b) status gate (Phase 3 UAT Test 2 follow-up): caught/hiding/peeking/
    //       on-floor/parked entities ignore their controller's move intent so
    //       (i) a caught hider does not drift against the escort leash (stage 7
    //       is the SOLE source of caught-hider motion — GAME-04/GAME-05), and
    //       (ii) a hiding/peeking/on-floor hider stays pinned to the spot
    //       (HIDE-03..HIDE-07). Without this gate, LocalInputController
    //       faithfully forwards the joystick vector even post-catch, which
    //       fought the escort leash and (with wall-clamping) trapped the
    //       player inside building geometry.
    // 'free' and 'escorting' are the only statuses that accept controller
    // motion. 'caught' covers both in-escort and parked-at-plinth hiders
    // (D-12: parked hiders keep status='caught'). 'hiding' covers both
    // spot-hidden and on-floor hiders (on-floor is status='hiding' with
    // floorSpot set, per src/sim/hiding/on-floor.ts).
    const isStatusFrozen =
      current.status !== 'free' && current.status !== 'escorting';
    const headstartGated =
      nextRound.phase === 'headstart' && current.role === 'hunter';
    const gatedMove =
      headstartGated || isStatusFrozen ? { x: 0, y: 0 } : intent.move;

    // Torch toggle.
    let torchOn = current.torchOn;
    if (intent.actions.toggleTorch) {
      torchOn = !torchOn;
      events.push({ type: 'torch-flicked', entityId, on: torchOn, tick: nextTick });
    }

    const from = current.position;

    // Speed ramp — composite of light-level penalty (P2) and surface-type multiplier
    // (Phase 08.1 Item 2). Both are folded into a single `speedRamp` multiplier so
    // their effects stack (dark + rough = extra slow; lit road = full-speed boost).
    let speedRamp = 1;
    if (mapData) {
      const torches: TorchLightSource[] =
        torchOn && state.playerFov !== null
          ? [{ origin: from, facingRad: current.facing, fovBitset: state.playerFov.tiles }]
          : [];
      const lightAtFrom = lightLevelAt(from, mapData, nextLightsState, torches, nextTick);
      speedRamp = lightSpeedRamp(lightAtFrom);

      // Phase 08.1 Item 2: surface walking-speed multiplier from the pre-rasterized
      // speedGrid. Sample the tile under the entity's CURRENT position (from, not
      // the intended destination) to avoid read-ahead artefacts near polygon edges.
      // speedGrid is optional (absent on sim-test minimal MapData → ramp unchanged).
      // Default 1.0 (scrub) matches surface-types.json and the buildSpeedGrid default.
      if (mapData.speedGrid) {
        const tx = Math.floor(from.x / mapData.tileWidth);
        const ty = Math.floor(from.y / mapData.tileHeight);
        if (tx >= 0 && ty >= 0 && tx < mapData.widthInTiles && ty < mapData.heightInTiles) {
          const surfaceSpeed = mapData.speedGrid[ty * mapData.widthInTiles + tx] ?? 1.0;
          speedRamp *= surfaceSpeed;
        }
      }
    }

    const canSprint = intent.actions.sprint && current.stamina > 0;
    const baseSpeed = canSprint ? P1_DEFAULT_BASE_SPEED * P1_SPRINT_MULTIPLIER : P1_DEFAULT_BASE_SPEED;
    const speed = baseSpeed * speedRamp;

    const intended: Vec2 = {
      x: from.x + gatedMove.x * speed * dt,
      y: from.y + gatedMove.y * speed * dt,
    };
    const to = mapData
      ? clampMotionAgainstWalls(from, intended, P1_CHARACTER_RADIUS, mapData)
      : intended;
    const nextFacing = lerpFacing(current.facing, intent.facing, dt, P1_DEFAULT_TURN_RATE);

    const moved = to.x !== from.x || to.y !== from.y;

    let updated: Entity = moved
      ? { ...current, position: to, facing: nextFacing, torchOn, lastPosition: from }
      : { ...current, facing: nextFacing, torchOn, lastPosition: from };

    // STAGE 5 — stamina.
    const profileId = hasProfile(updated.characterId) ? updated.characterId : DEFAULT_CHARACTER_ID;
    const profile = getProfile(profileId);
    const staminaIntent: Intent = { ...intent, move: gatedMove };
    const staminaRes = applyStamina(updated, staminaIntent, dt, profile, nextTick);
    updated = staminaRes.next;
    for (const ev of staminaRes.events) events.push(ev);

    nextEntities[entityId] = updated;
    if (moved) {
      events.push({ type: 'entity-moved', entityId, from, to, tick: nextTick });
    }

    // STAGE 13a — hide intent consumption (compute candidate ONCE per hider; reuse
    // the hoisted `occupied` set). For e0 specifically, cache the result for stage
    // 11 reuse (WARNING-7 single-compute-per-tick guarantee).
    //
    // UAT Test 7 follow-up: hide intent is consumed during BOTH `headstart` and
    // `active` phases. The whole point of head-start is that hiders hide before
    // the hunters are released; gating this on `active` alone blocked hiding
    // during exactly the phase designed for it. `ended` (team-switch window) is
    // still excluded so the diegetic transition isn't interrupted.
    const hideEligiblePhase =
      nextRound.phase === 'active' || nextRound.phase === 'headstart';
    if (hideEligiblePhase && updated.role === 'hider') {
      const prevActiveId =
        state.derived.hideCandidate?.entityId === entityId
          ? state.derived.hideCandidate.spotId
          : null;
      const activeSpot = mapData
        ? findActiveHidingSpot(updated, profile, mapData, occupied, prevActiveId)
        : null;

      if (entityId === 'e0') {
        e0CandidateComputed = activeSpot;
      }

      if (intent.actions.toggleHide) {
        const tog = applyHideToggle(updated, activeSpot, nextTick);
        nextEntities[entityId] = tog.next;
        for (const ev of tog.events) events.push(ev);
      } else if (intent.actions.dropToFloor) {
        const drop = applyDropToFloorIntent(updated, profile, nextTick);
        nextEntities[entityId] = drop.next;
        for (const ev of drop.events) events.push(ev);
      }

      // STAGE 13b — peek held state. applyPeekIntent honours
      // peekSuppressedUntilRelease (BLOCKER-3) internally — see peek.ts.
      const final = nextEntities[entityId]!;
      const peekRes = applyPeekIntent(final, intent.actions.peek, nextTick);
      nextEntities[entityId] = peekRes.next;
      for (const ev of peekRes.events) events.push(ev);
    }
  }

  // STAGE 6 — catch detection (active only).
  if (nextRound.phase === 'active') {
    const catchRes = detectCatches(
      { ...state, entities: nextEntities, tick: nextTick, round: nextRound },
      nextTick,
    );
    for (const [id, upd] of Object.entries(catchRes.updates)) nextEntities[id] = upd;
    for (const ev of catchRes.events) {
      events.push(ev);
      // P6 D-04 — catch: speaker = ev.hunterId, tag = 'catch'.
      if (ev.type === 'catch') {
        maybeEmitCharacterSpoke(ev.hunterId, 'catch', nextTick, nextEntities, rngForTick, events);
      }
    }
  }

  // STAGE 7 — escort leash. Emits NO entity-moved events (WARNING-11).
  if (mapData) {
    const escortUpd = applyEscortLeash(
      { ...state, entities: nextEntities, tick: nextTick, round: nextRound },
      mapData,
      dt,
    );
    for (const [id, upd] of Object.entries(escortUpd)) nextEntities[id] = upd;
  }

  // STAGE 8 — drop-off.
  if (nextRound.phase === 'active') {
    const dropRes = applyDropOff(
      { ...state, entities: nextEntities, tick: nextTick, round: nextRound },
      mapData,
      nextTick,
    );
    for (const [id, upd] of Object.entries(dropRes.updates)) nextEntities[id] = upd;
    for (const ev of dropRes.events) events.push(ev);
  }

  // STAGE 9 — free-all.
  if (nextRound.phase === 'active') {
    const freeRes = applyFreeAll(
      { ...state, entities: nextEntities, tick: nextTick, round: nextRound },
      mapData,
      nextTick,
    );
    for (const [id, upd] of Object.entries(freeRes.updates)) nextEntities[id] = upd;
    for (const ev of freeRes.events) {
      events.push(ev);
      // P6 D-04 — hider-released: speaker = ev.liberatorId, tag = 'free-all'.
      // CONTEXT mapping: bundles author 'free-all' quotes; reuse the bucket.
      if (ev.type === 'hider-released') {
        maybeEmitCharacterSpoke(ev.liberatorId, 'free-all', nextTick, nextEntities, rngForTick, events);
      }
    }
  }

  // STAGE 10 — foxguard.
  if (nextRound.phase === 'active' && mapData) {
    // Snapshot hunter ids first so we don't iterate a set we're mutating.
    const hunterIds = Object.values(nextEntities)
      .filter((e) => e.role === 'hunter')
      .map((e) => e.id)
      .sort();
    for (const hunterId of hunterIds) {
      const hunter = nextEntities[hunterId];
      if (!hunter) continue;
      const worldForFox = { ...state, entities: nextEntities, tick: nextTick, round: nextRound };
      const conds = evaluateFoxguardConditions(hunter, worldForFox, mapData, dt);
      const streakRes = updateFoxguardStreak(hunter, conds, nextTick);
      nextEntities[hunterId] = streakRes.next;
      if (streakRes.graceElapsed) {
        const shouter = pickShouter(
          { ...state, entities: nextEntities, tick: nextTick, round: nextRound },
          mapData,
          nextTick,
          rngForTick.foxguard,
        );
        if (shouter) {
          nextEntities[shouter.id] = { ...shouter, lastFoxguardShoutTick: nextTick };
          events.push({
            type: 'foxguard-called',
            hunterId: hunterId,
            shouterId: shouter.id,
            tick: nextTick,
          });
          // P6 D-04 — foxguard-called: speaker = shouter.id, tag = 'foxguard-called'.
          maybeEmitCharacterSpoke(
            shouter.id,
            'foxguard-called',
            nextTick,
            nextEntities,
            rngForTick,
            events,
          );
        }
      }
    }
  }

  // STAGE 11 — hide-candidate DERIVED-FIELD write (WARNING-7 single-compute path).
  //   - If stage 13a computed e0CandidateComputed, reuse it.
  //   - Otherwise (no e0 intent this tick), compute once with the hoisted `occupied`.
  //   - Write to nextDerived only when spotId differs from previous derived value.
  let nextDerived: DerivedState = state.derived;
  if (mapData) {
    const player = nextEntities['e0'];
    if (player && player.role === 'hider' && player.status === 'free') {
      const prev = state.derived.hideCandidate;
      const prevId = prev && prev.entityId === 'e0' ? prev.spotId : null;

      let spotId: string | null;
      if (e0CandidateComputed !== undefined) {
        // Reuse — avoid the double-compute the previous plan had here.
        spotId = e0CandidateComputed?.id ?? null;
      } else {
        const profileId = hasProfile(player.characterId) ? player.characterId : DEFAULT_CHARACTER_ID;
        const profile = getProfile(profileId);
        const spot = findActiveHidingSpot(player, profile, mapData, occupied, prevId);
        spotId = spot?.id ?? null;
      }

      if (prevId !== spotId) {
        nextDerived = { hideCandidate: { entityId: 'e0', spotId } };
      }
    } else if (state.derived.hideCandidate !== null) {
      // Player no longer a free hider — clear the derived slot.
      nextDerived = { hideCandidate: null };
    }
  }

  // STAGE 12 — peek auto-release (finger-stuck guard).
  // BLOCKER-3: checkPeekAutoRelease sets peekSuppressedUntilRelease=true so
  // stage 13b's applyPeekIntent on the NEXT tick (with peekHeld still true from
  // a stuck finger) does NOT re-engage peek. Plan 05 Task 3 implementation.
  for (const id of Object.keys(nextEntities)) {
    const e = nextEntities[id]!;
    if (e.status === 'peeking') {
      const res = checkPeekAutoRelease(e, nextTick);
      nextEntities[id] = res.next;
      for (const ev of res.events) events.push(ev);
    }
  }

  // STAGE 14 — player torch FOV (P2 unchanged). Recompute AFTER entity movement
  // so the bitset reflects the just-applied position. Always for entity 'e0' in
  // P2/P3; P4 generalises to per-entity AI FOV at 10 Hz via WorldState.entitiesFov.
  let nextPlayerFov: FovSnapshot | null = null;
  const player = nextEntities['e0'];
  if (player && player.torchOn && mapData) {
    const bitset = getTorchBitset(mapData.widthInTiles, mapData.heightInTiles);
    const tileX = player.position.x / mapData.tileWidth;
    const tileY = player.position.y / mapData.tileHeight;
    computeTorchFov(tileX, tileY, player.facing, mapData.grid, mapData.widthInTiles, bitset);
    nextPlayerFov = { tiles: bitset, tick: nextTick };
  }

  // Update caughtCount to reflect the final-of-tick entity statuses so next
  // tick's round FSM sees the correct count.
  const finalCaughtCount = Object.values(nextEntities)
    .filter((e) => e.role === 'hider' && e.status === 'caught').length;
  nextRound = { ...nextRound, caughtCount: finalCaughtCount };

  // =========================================================================
  // P4 STAGES 16..23 — AI orchestration
  // Only run when mapData is present (golden-hash fixture runs without mapData).
  // =========================================================================
  // Use flipped blackboards if Stage 0 ran flipRolesAndReset this tick (D-33 reset).
  let nextHunterBlackboard = (nextFlippedBlackboards ?? state.teamBlackboards).hunter;
  let nextHiderBlackboard = (nextFlippedBlackboards ?? state.teamBlackboards).hider;
  let nextLastAdultCommentaryTick = state.lastAdultCommentaryTick;

  if (mapData) {
    const awarenessCache = getAwarenessCacheData(state.rngSeed);

    // -----------------------------------------------------------------------
    // STAGE 16 — Per-AI-entity perception build + BT decide (cadence-gated D-05)
    // -----------------------------------------------------------------------
    // Build a snapshot of the world state for AI perception (immutable during this stage).
    const worldForAi: WorldState = {
      ...state,
      entities: nextEntities,
      tick: nextTick,
      round: nextRound,
      lightsState: nextLightsState,
      playerFov: nextPlayerFov,
      teamBlackboards: { hunter: nextHunterBlackboard, hider: nextHiderBlackboard },
      lastAdultCommentaryTick: nextLastAdultCommentaryTick,
      lastBenchedNpcId: state.lastBenchedNpcId,
      derived: nextDerived,
    };

    // Sorted entity ids for deterministic processing (Pitfall 10).
    const aiEntityIds = Object.keys(nextEntities).sort();

    for (const entityId of aiEntityIds) {
      if (entityId === 'e0') continue; // player entity: LocalInputController handles it
      const entity = nextEntities[entityId];
      if (!entity) continue;

      const controller = getAiController(entityId);
      if (!controller) continue;

      // Build per-observer awareness map (Map<targetId, number>) for this entity.
      let observerAwarenessMap = awarenessCache.get(entityId);
      if (!observerAwarenessMap) {
        observerAwarenessMap = new Map<string, AwarenessCacheEntry>();
        awarenessCache.set(entityId, observerAwarenessMap);
      }

      // Build a flat awareness scalar map for perception (Map<targetId, awareness>).
      const awarenessScalarMap = new Map<string, number>();
      for (const [targetId, entry] of observerAwarenessMap) {
        awarenessScalarMap.set(targetId, entry.awareness);
      }

      // WR-02 fix: AI hunters use the radius+LOS path (buildPerception falls
      // back to it when bitset is null) rather than reusing the PLAYER's FOV
      // bitset. The previous behaviour let every AI hunter see whatever the
      // player-entity's torch cone saw — so a hunter with torch off saw
      // through the player's torch, and when the player was a hider all AI
      // hunters were effectively omniscient within P4_AWARENESS_MAX_RANGE_TILES.
      //
      // A proper per-hunter FOV bitset (computeTorchFov per hunter at 10 Hz +
      // per-entity bitset cache) is deferred post-P4; this fallback is the
      // minimum-correctness patch noted in the review.
      const agentFovBitset: Uint32Array | null = null;

      const perception = buildPerception(
        entity,
        worldForAi,
        mapData,
        agentFovBitset,
        controller.getEntityRng(), // D-06 per-entity seeded RNG sub-stream
        awarenessScalarMap,
        _recentSoundEventsThisTick,
      );

      // BLOCKER-1 (D-16) — CR-02 FIX: For hunters, compute the per-entry
      // duplicate-hunter counts and seed them on agent.investigateDuplicateCounts
      // BEFORE calling controller.decide(). AIController.decide() passes this map
      // into scoreHunterBranches → computeInvestigateUtility so the argmaxBranch
      // call inside decide() sees the full AI-12 formula with duplicate penalty.
      //
      // The previous implementation overwrote agent.utilities AFTER decide() had
      // already run argmaxBranch — a no-op because winningBranch was committed
      // from the naïve (duplicate=0) score. This is the entire BLOCKER-1 fix.
      if (entity.role === 'hunter' && perception.memory.length > 0) {
        const duplicateCounts = new Map<string, number>();
        for (const entry of perception.memory) {
          duplicateCounts.set(entry.id, huntersAlreadyHeadingToEntry(entry.id, entityId));
        }
        const agentRef = (controller as unknown as {
          agent: { investigateDuplicateCounts?: ReadonlyMap<string, number> };
        }).agent;
        agentRef.investigateDuplicateCounts = duplicateCounts;
      }

      // Step the BT (cadence-gated inside AIController.decide).
      const aiIntent = controller.decide(perception, 1 / 30);

      // Store intent for processing in stages 21/23.
      // CR-01: track shout intents in a module-level Set so we don't mutate
      // the Entity record (which is the same reference as state.entities[id]
      // when no prior stage replaced it, breaking the tick-purity invariant).
      if (aiIntent.actions.shout) {
        _aiShoutsThisTick.add(entityId);
      }

      // Update _hunterTargetBlackboardEntryId (BLOCKER-1): read targetBlackboardEntryId
      // from the agent context via the controller.
      if (entity.role === 'hunter') {
        const agentCtx = (controller as unknown as { agent: { targetBlackboardEntryId?: string | null } }).agent;
        _hunterTargetBlackboardEntryId.set(entityId, agentCtx.targetBlackboardEntryId ?? null);
      }
    }

    // -----------------------------------------------------------------------
    // STAGE 17 — Awareness step + blackboard write + _hunterLastSightingTick (BLOCKER-4)
    // -----------------------------------------------------------------------
    // For each hunter observer, advance/decay awareness toward each other entity.
    //
    // WR-04 fix: gate advance/decay to the 10 Hz hunter decide cadence so the
    // accumulator matches the "per perception tick (10 Hz)" semantics of
    // P4_AWARENESS_BASE_RATE / P4_AWARENESS_DECAY_RATE. Previously this loop
    // ran at the full 30 Hz sim rate, tripling the effective rate and making
    // hunters reach Spotted ~3x faster than the constants were tuned for.
    // We share P4_HUNTER_DECIDE_PERIOD_TICKS (3) with the BT decide cadence —
    // both are 10 Hz so retuning stays coherent.
    //
    // Plan 06-10 phase gate (UAT Gap 6): no awareness/shout work during
    // headstart or ended (team-switch) windows. Hunters are physically frozen
    // during headstart anyway (stage 3 movement gate) but the awareness
    // accumulator would still tick if a hider walked into range. Gating here
    // keeps the entire sense→shout chain phase-correct.
    if (nextRound.phase === 'active') {
    const awarenessStepsThisTick = nextTick % P4_HUNTER_DECIDE_PERIOD_TICKS === 0;
    const hunterIds = Object.values(nextEntities)
      .filter((e) => e.role === 'hunter')
      .map((e) => e.id)
      .sort();

    for (const hunterId of awarenessStepsThisTick ? hunterIds : []) {
      const hunter = nextEntities[hunterId];
      if (!hunter) continue;

      let observerMap = awarenessCache.get(hunterId);
      if (!observerMap) {
        observerMap = new Map<string, AwarenessCacheEntry>();
        awarenessCache.set(hunterId, observerMap);
      }

      const maxRangePx = getTuning().awarenessMaxRangeTiles * mapData.tileWidth;

      // For each potential target (sorted for determinism)
      const targetIds = Object.keys(nextEntities).sort();
      for (const targetId of targetIds) {
        if (targetId === hunterId) continue;
        const target = nextEntities[targetId];
        if (!target) continue;
        if (target.role !== 'hider') continue; // hunters only track hiders for awareness
        // Caught hiders are already under hunter control — skip awareness tracking for them.
        // This prevents blackboard entries for caught hiders from blocking foxguard condition 4.
        if (target.status === 'caught') continue;

        const distPx = Math.hypot(
          target.position.x - hunter.position.x,
          target.position.y - hunter.position.y,
        );

        // WR-02 fix: AI hunters use range-only (P4_AWARENESS_MAX_RANGE_TILES)
        // rather than the PLAYER's FOV bitset. The previous code used
        // nextPlayerFov for every hunter, which (a) let hunters without torch
        // see through the player's torch, and (b) when the player was a hider
        // left fov=null and triggered the range-only fallback anyway — so
        // rendering the bitset branch never matched the intended D-09 "hunter
        // uses their own cone" semantics.
        //
        // Per-hunter torch FOV + LOS checks are deferred post-P4 (see the
        // Stage 16 agentFovBitset comment). For now, range-only matches the
        // non-hunter perception behaviour.
        const inFov = distPx <= maxRangePx;

        const existing: AwarenessCacheEntry = observerMap.get(targetId) ?? {
          awareness: 0,
          stableTicks: 0,
          lastSeenTick: -1,
        };
        const prevAwareness = existing.awareness;

        let nextAwareness: number;
        if (inFov && distPx <= maxRangePx) {
          // WR-03 fix: use real sightabilityOf × lightLevelAt (same formula as
          // buildPerception.ts L161-172). Previously hardcoded 1.0, which made
          // hiders in dark rooms / bushes / dark clothes accrue awareness at the
          // same rate as hiders standing under a torch, bypassing the entire
          // FEAR / hideSkill / light integration from D-09.
          // UAT Test 2 follow-up: P4_AI_AMBIENT_LIGHT_FLOOR applied to match
          // the identical fix in src/sim/ai/perception.ts. Without this floor,
          // awareness accumulation (and thus the ?/! telegraphs + Spotted
          // blackboard entries that drive the chase branch) stalls in dark
          // outdoor areas that lack authored lights.
          const rawLight = lightLevelAt(
            target.position,
            mapData,
            nextLightsState,
            [],
            nextTick,
          );
          const light = Math.max(rawLight, P4_AI_AMBIENT_LIGHT_FLOOR);
          const sightability = sightabilityOf(target, mapData) * light;
          nextAwareness = advanceAwareness(sightability, distPx, maxRangePx, prevAwareness);
        } else {
          // Target not visible — decay awareness
          nextAwareness = decayAwareness(prevAwareness);
        }

        // Stability check for Spotted level
        const { nextStableTicks, ready } = stepStabilityCheck(existing.stableTicks, nextAwareness);

        // Emit boundary event if level changed (awareness-changed BEFORE shout — Stage 17)
        const boundaryEv = emitBoundaryEvent(prevAwareness, nextAwareness, hunterId, nextTick);
        if (boundaryEv) {
          events.push(boundaryEv);
        }

        // BLOCKER-4: record last sighting tick when hunter reaches alerted or spotted
        const nextLevel = classifyLevel(nextAwareness);
        if (nextLevel === 'alerted' || nextLevel === 'spotted') {
          _hunterLastSightingTick.set(hunterId, nextTick);
        }

        // Write blackboard at Alerted or Spotted (D-12)
        if (nextLevel === 'alerted' && classifyLevel(prevAwareness) !== 'alerted' && classifyLevel(prevAwareness) !== 'spotted') {
          // First time reaching Alerted: write low-confidence entry
          nextHunterBlackboard = upgradeOrWriteBlackboardEntry(
            nextHunterBlackboard,
            hunterId,
            { ...target.position },
            'low',
            nextTick,
          );
        }

        // Spotted stability commit: emit shout + write high-confidence entry
        if (ready && nextLevel === 'spotted') {
          // Check we haven't already emitted shout for this pair this tick
          const prevLevel = classifyLevel(prevAwareness);
          if (prevLevel !== 'spotted' || existing.stableTicks < 2) {
            // Emit shout event (Stage 17 — awareness-changed fires first above)
            events.push({
              type: 'shout',
              sourceId: hunterId,
              position: { ...hunter.position },
              tick: nextTick,
            });
            // Push to sound events for next-tick perception
            _recentSoundEventsThisTick.push({
              kind: 'shout',
              sourceId: hunterId,
              position: { ...hunter.position },
              tick: nextTick,
            });
            // Upgrade blackboard to high-confidence
            nextHunterBlackboard = upgradeOrWriteBlackboardEntry(
              nextHunterBlackboard,
              hunterId,
              { ...target.position },
              'high',
              nextTick,
            );
          }
        }

        // Update cache entry
        observerMap.set(targetId, {
          awareness: nextAwareness,
          stableTicks: nextStableTicks,
          lastSeenTick: inFov ? nextTick : existing.lastSeenTick,
        });
      }
    }
    } // end Plan 06-10 phase gate (STAGE 17 — awareness only runs when active)

    // -----------------------------------------------------------------------
    // STAGE 18 — Blackboard decay (pruneBlackboard)
    // -----------------------------------------------------------------------
    // Decay runs in EVERY phase: this prunes stale entries written during a
    // prior 'active' phase that should not survive into the next round. Not
    // gated by Plan 06-10.
    nextHunterBlackboard = pruneBlackboard(nextHunterBlackboard, nextTick);
    nextHiderBlackboard = pruneBlackboard(nextHiderBlackboard, nextTick);

    // -----------------------------------------------------------------------
    // STAGE 21 — Shout propagation + dog-barked event translation (INFO-11)
    // -----------------------------------------------------------------------
    // Process shout intents from AI entities: dog role → dog-barked, others → shout.
    // CR-01: read from the module-level _aiShoutsThisTick Set (no Entity mutation).
    //
    // Plan 06-10 phase gate (UAT Gap 1/6): no shout/bark events during
    // headstart or ended (team-switch). Headstart is the silent-countdown
    // window; the team-switch window is diegetically silent per CONTEXT D-15.
    if (nextRound.phase === 'active') {
    for (const entityId of aiEntityIds) {
      if (entityId === 'e0') continue;
      if (!_aiShoutsThisTick.has(entityId)) continue;
      const entity = nextEntities[entityId];
      if (!entity) continue;

      if (entity.role === 'dog') {
        // INFO-11: dog role emits dog-barked, not shout
        events.push({
          type: 'dog-barked',
          dogId: entityId,
          position: { ...entity.position },
          tick: nextTick,
        });
        _recentSoundEventsThisTick.push({
          kind: 'dog-bark',
          sourceId: entityId,
          position: { ...entity.position },
          tick: nextTick,
        });
      } else {
        // Human role shout (non-dog)
        const sourceTile: [number, number] = [
          Math.floor(entity.position.x / mapData.tileWidth),
          Math.floor(entity.position.y / mapData.tileHeight),
        ];
        // Propagate to all listeners via isShoutAudible
        for (const listenerId of Object.keys(nextEntities).sort()) {
          if (listenerId === entityId) continue;
          const listener = nextEntities[listenerId];
          if (!listener) continue;
          const listenerTile: [number, number] = [
            Math.floor(listener.position.x / mapData.tileWidth),
            Math.floor(listener.position.y / mapData.tileHeight),
          ];
          const listenerProfileId = hasProfile(listener.characterId) ? listener.characterId : 'charlie';
          const listenerProfile = getProfile(listenerProfileId);
          const hearingRange = listenerProfile.hear.base;
          const { audible } = isShoutAudible(sourceTile, listenerTile, hearingRange, mapData.grid);
          if (audible) {
            _recentSoundEventsThisTick.push({
              kind: 'shout',
              sourceId: entityId,
              position: { ...entity.position },
              tick: nextTick,
            });
            break; // one entry per shout source (listeners will filter by distance in perception)
          }
        }
        events.push({
          type: 'shout',
          sourceId: entityId,
          position: { ...entity.position },
          tick: nextTick,
        });
      }
    }
    } // end Plan 06-10 phase gate (STAGE 21 — shout/dog-barked only when active)

    // -----------------------------------------------------------------------
    // STAGE 22 — Fear accumulators (advanceFear per non-NPC entity, BLOCKER-4)
    // -----------------------------------------------------------------------
    // Plan 06-10 phase gate (UAT Gap 6): closes the dark-fear-accumulator
    // leak during headstart that surfaced as the spurious T-10s/T-5s `!`
    // bubbles. Player starts headstart in dark with torch off; without this
    // gate, fear crosses NERVOUS at ~3s and AFRAID at ~5.5s — well inside
    // the 20s headstart window. Also closes the symmetric leak during the
    // 'ended' team-switch window per CONTEXT D-15.
    if (nextRound.phase === 'active') {
    const worldForFear: WorldState = {
      ...state,
      entities: nextEntities,
      tick: nextTick,
      round: nextRound,
      lightsState: nextLightsState,
      playerFov: nextPlayerFov,
      teamBlackboards: { hunter: nextHunterBlackboard, hider: nextHiderBlackboard },
      lastAdultCommentaryTick: nextLastAdultCommentaryTick,
      lastBenchedNpcId: state.lastBenchedNpcId,
      derived: nextDerived,
    };

    for (const entityId of Object.keys(nextEntities).sort()) {
      const entity = nextEntities[entityId];
      if (!entity) continue;
      // Fear applies to hunters and hiders; skip dogs, adults, and NPCs without profiles
      if (entity.role !== 'hunter' && entity.role !== 'hider') continue;
      if (!hasProfile(entity.characterId)) continue;

      const profileId = entity.characterId;
      const profile = getProfile(profileId);

      // BLOCKER-4: pass lastSightingTick from the module-level map
      const lastSightingTick =
        entity.role === 'hunter'
          ? (_hunterLastSightingTick.get(entityId) ?? null)
          : null;

      // Build minimal perception for fear accumulator (it uses _perception: unused)
      const minimalPerception = {
        self: {
          id: entity.id,
          characterId: entity.characterId,
          position: entity.position,
          facing: entity.facing,
          role: entity.role,
          status: entity.status,
          torchOn: entity.torchOn,
          stamina: entity.stamina,
          fear: entity.fear,
          awareness: new Map<string, number>(),
        },
        visible: [],
        heard: [],
        memory: [],
        map: mapData,
        round: {
          phase: nextRound.phase,
          roundNumber: nextRound.roundNumber,
          tickInRound: nextTick - (nextRound.startTick ?? 0),
        },
        rng: () => 0.5,
        tick: nextTick,
      };

      const fearResult = advanceFear(
        entity,
        profile,
        mapData,
        worldForFear,
        minimalPerception,
        lastSightingTick,
        nextTick,
        {
          ambientLightFloor: P4_AI_AMBIENT_LIGHT_FLOOR,
          suppressIsolationWhenNoTeammatesExist: true,
        },
      );

      nextEntities[entityId] = { ...entity, fear: fearResult.nextFear };
      for (const ev of fearResult.boundaryEvents) events.push(ev);
    }
    } // end Plan 06-10 phase gate (STAGE 22 — advanceFear only when active)

    // -----------------------------------------------------------------------
    // STAGE 23 — Adult commentary rate-limit (NPC-04, D-28)
    // -----------------------------------------------------------------------
    // Plan 06-10 phase gate (UAT Gap 1/6): adults stay quiet during the
    // headstart silent-countdown window AND during the team-switch window
    // (CONTEXT D-15). Without this gate, an adult NPC with a stale
    // commentaryToEmit scratch from the previous round could fire on the
    // first tick of the new round's headstart.
    if (nextRound.phase === 'active') {
    for (const entityId of aiEntityIds) {
      if (entityId === 'e0') continue;
      const entity = nextEntities[entityId];
      if (!entity || entity.role !== 'adult') continue;

      const controller = getAiController(entityId);
      if (!controller) continue;

      // Read adultComment from the agent's intent via the controller's cached intent.
      // The AIController exposes the last computed intent via decide(); we peek at the agent.
      const agentRec = (controller as unknown as { agent: Record<string, unknown> }).agent;
      const adultComment = agentRec['commentaryToEmit'] as
        | { eventTag: string; lineIndex: number }
        | undefined;

      if (adultComment) {
        const cooldownElapsed =
          nextLastAdultCommentaryTick === null ||
          (nextTick - nextLastAdultCommentaryTick) >= P4_ADULT_COMMENTARY_GLOBAL_COOLDOWN_TICKS;

        if (cooldownElapsed) {
          events.push({
            type: 'adult-commented',
            adultId: entityId,
            eventTag: adultComment.eventTag as EventTag,
            lineIndex: adultComment.lineIndex,
            tick: nextTick,
          });
          nextLastAdultCommentaryTick = nextTick;
          // Clear the commentary scratch to avoid re-firing next tick
          agentRec['commentaryToEmit'] = undefined;
        }
      }
    }
    } // end Plan 06-10 phase gate (STAGE 23 — adult commentary only when active)
  } // end if (mapData) for P4 stages

  return {
    state: {
      tick: nextTick,
      entities: nextEntities,
      rngSeed: state.rngSeed,
      lightsState: nextLightsState,
      playerFov: nextPlayerFov,
      round: nextRound,
      derived: nextDerived,
      // P4 D-13: updated blackboards from stages 17/18.
      teamBlackboards: { hunter: nextHunterBlackboard, hider: nextHiderBlackboard },
      lastAdultCommentaryTick: nextLastAdultCommentaryTick,
      lastBenchedNpcId: state.lastBenchedNpcId,
    },
    events,
  };
}
